<template>
  <div class="custom-fields">
    <div v-for="(field, index) in fields" :key="index" class="custom-field-container">
      <div v-if="field === 'Name'">
        <div
          class="input-container"
          :class="{ 'input-error': $v.user_name.$error && !isFieldFocused('name') }"
        >
          <input
            v-model.trim="user_name"
            class="custom-input"
            placeholder=" "
            type="text"
            @input="delayTouch($v.user_name)"
            @focus="handleFocus('name')"
            @blur="handleBlur('name')"
          />
          <label class="custom-label">
            <span class="label-text">{{ $t('placeholder_name') }}</span>
            <span v-if="isOptionalFields('Name')" class="label-optional">
              ({{ $t('optional') }})
            </span>
          </label>
        </div>
        <div
          v-if="
            $v.user_name.$error &&
            !isFieldFocused('name') &&
            (validationAttempted || $v.user_name.$dirty)
          "
          class="error-message"
        >
          {{ $t('name_error') }}
        </div>
      </div>

      <div v-if="field === 'Email'">
        <div
          class="input-container"
          :class="{ 'input-error': $v.user_email.$error && !isFieldFocused('email') }"
        >
          <input
            v-model.trim="user_email"
            type="email"
            placeholder=" "
            @input="delayTouch($v.user_email)"
            @focus="handleFocus('email')"
            @blur="handleBlur('email')"
          />
          <label class="custom-label">
            <span class="label-text">{{ $t('placeholder_email') }}</span>
            <span v-if="isOptionalFields('Email')" class="label-optional">
              ({{ $t('optional') }})
            </span>
          </label>
        </div>
        <div
          v-if="
            $v.user_email.$error &&
            !isFieldFocused('email') &&
            (validationAttempted || $v.user_email.$dirty)
          "
          class="error-message"
        >
          {{ $t('invalid_email') }}
        </div>
      </div>

      <div v-if="field === 'Gender'">
        <div
          class="gender-div"
          :class="{ 'input-error': $v.gender.$error && !isFieldFocused('gender') }"
        >
          <p style="color: #777777; margin-left: 10px">
            {{ placeholder('element_gender', 'Gender') }}
          </p>
          <input
            id="male"
            v-model.trim="gender"
            value="male"
            type="radio"
            name="radio-group"
            checked
            @input="delayTouch($v.gender)"
            @focus="handleFocus('gender')"
            @blur="handleBlur('gender')"
          />
          <label for="male" :style="{ paddingRight: '20px', color: '#777777' }">
            {{ $t('gender_type_male') }}
          </label>
          <input
            id="female"
            v-model.trim="gender"
            value="female"
            type="radio"
            name="radio-group"
            @input="delayTouch($v.gender)"
            @focus="handleFocus('gender')"
            @blur="handleBlur('gender')"
          />
          <label for="female" :style="{ paddingRight: '10px', color: '#777777' }">
            {{ $t('gender_type_female') }}
          </label>
        </div>
        <div
          v-if="
            $v.gender.$error &&
            !isFieldFocused('gender') &&
            (validationAttempted || $v.gender.$dirty)
          "
          class="error-message"
        >
          {{ $t('gender_error') }}
        </div>
      </div>

      <div v-if="field === 'Country'">
        <div
          class="input-container"
          :class="{ 'div-error': $v.user_country.$error && !isFieldFocused('country') }"
        >
          <div class="country-select" :class="{ 'has-value': user_country }">
            <label class="custom-label">
              <span class="label-text">{{ $t('placeholder_country') }}</span>
              <span v-if="isOptionalFields('Country')" class="label-optional">
                ({{ $t('optional') }})
              </span>
            </label>
            <v-select
              v-model="user_country"
              label="country"
              :options="countries"
              placeholder=" "
              append-to-body
              @open="isCountrySelectOpen = true"
              @close="isCountrySelectOpen = false"
              @focus="handleFocus('country')"
              @blur="handleBlur('country')"
            />
          </div>
        </div>
        <div
          v-if="
            $v.user_country.$error &&
            !isFieldFocused('country') &&
            (validationAttempted || $v.user_country.$dirty)
          "
          class="error-message"
        >
          {{ $t('country_error') }}
        </div>
      </div>

      <div v-if="field === 'City'">
        <div
          class="input-container"
          :class="{ 'input-error': $v.user_city.$error && !isFieldFocused('city') }"
        >
          <input
            v-model.trim="user_city"
            type="text"
            placeholder=" "
            @input="delayTouch($v.user_city)"
            @focus="handleFocus('city')"
            @blur="handleBlur('city')"
          />
          <label class="custom-label">
            <span class="label-text">{{ $t('placeholder_city') }}</span>
            <span v-if="isOptionalFields('City')" class="label-optional">
              ({{ $t('optional') }})
            </span>
          </label>
        </div>
        <div
          v-if="
            $v.user_city.$error &&
            !isFieldFocused('city') &&
            (validationAttempted || $v.user_city.$dirty)
          "
          class="error-message"
        >
          {{ $t('city_error') }}
        </div>
      </div>

      <div v-if="field === 'Phone Number' || field === 'Phone'">
        <div class="phone-code" style="display: flex; flex-direction: column">
          <div style="display: flex">
            <div class="phone-flag" @click.prevent="openCountryModal">
              <span>{{ flag }}&#160;{{ code }}</span>
            </div>
            <div
              class="input-container"
              :class="{ 'input-error': $v.user_phone.$error && !isFieldFocused('phone') }"
            >
              <input
                v-model.trim="user_phone"
                type="tel"
                placeholder=" "
                @input="delayTouch($v.user_phone)"
                @focus="handleFocus('phone')"
                @blur="handleBlur('phone')"
                @keyup="numberFormat"
              />
              <label class="custom-label">
                <span class="label-text">{{ $t('placeholder_phone') }}</span>
                <span
                  v-if="isOptionalFields(field === 'Phone Number' ? 'Phone Number' : 'Phone')"
                  class="label-optional"
                >
                  ({{ $t('optional') }})
                </span>
              </label>
            </div>
          </div>
          <div
            v-if="
              $v.user_phone.$error &&
              !isFieldFocused('phone') &&
              (validationAttempted || $v.user_phone.$dirty)
            "
            class="error-message"
          >
            {{ $t('phone_error') }}
          </div>
          <transition name="modal">
            <div v-if="countryModal">
              <country-selector @select="onSelect" @close="closeCountryModal" />
            </div>
          </transition>
        </div>
      </div>

      <input
        v-if="field === 'Year Born' || field === 'Date of Birth' || field === 'DOB'"
        v-model.trim="user_dob"
        style="margin-top: 15px"
        type="hidden"
        @input="delayTouch($v.user_dob)"
      />

      <div v-if="field === 'Year Born' || field === 'Date of Birth' || field === 'DOB'">
        <div
          v-click-outside="handleClickOutside"
          class="input-container"
          :class="{
            'input-error': $v.user_dob.$error && !isFieldFocused('dob'),
            'has-value': birthday,
          }"
        >
          <Datepicker
            ref="dobDatepicker"
            v-model.trim="birthday"
            placeholder=""
            class="Date-picker"
            initial-view="year"
            :language="datePickerLanguage"
            :full-month-name="false"
            :open-date="new Date('1990/01/01')"
            format="MM/dd/yyyy"
            @opened="
              scrollToDatePicker
              isDatePickerOpen = true
            "
            @focus="handleFocus('dob')"
            @blur="handleBlur('dob')"
            @input="handleDobInput"
          />
          <label class="custom-label">
            <span class="label-text">{{ $t('placeholder_dob') }}</span>
            <span v-if="isOptionalFields('Date of Birth')" class="label-optional">
              ({{ $t('optional') }})
            </span>
          </label>
        </div>
        <div
          v-if="
            $v.user_dob.$error &&
            !isFieldFocused('dob') &&
            (validationAttempted || $v.user_dob.$dirty)
          "
          class="error-message"
        >
          {{ $t('dob_error') }}
        </div>
      </div>

      <input v-model="user_dob" type="hidden" />
      <div v-if="field === 'Phone PIN'">
        <div
          class="input-container"
          :class="{ 'input-error': $v.phone_pin.$error && !isFieldFocused('pin') }"
        >
          <input
            v-model="phone_pin"
            v-mask="pinMask"
            type="tel"
            placeholder=" "
            @input="delayTouch($v.phone_pin)"
            @focus="handleFocus('pin')"
            @blur="handleBlur('pin')"
          />
          <label class="custom-label">
            <span class="label-text">{{ $t('enter_pin') }}</span>
            <span v-if="isOptionalFields('Phone PIN')" class="label-optional">
              ({{ $t('optional') }})
            </span>
          </label>
        </div>
        <div
          v-if="
            $v.phone_pin.$error &&
            !isFieldFocused('pin') &&
            (validationAttempted || $v.phone_pin.$dirty)
          "
          class="error-message"
        >
          {{ $t('pin_error') }}
        </div>
      </div>

      <template v-if="userCreatedCustomFields.length > 0">
        <div
          v-for="(customField, fieldIndex) in userCreatedCustomFields"
          :key="fieldIndex"
          style="width: 320px"
        >
          <template v-if="customField.type === 'select'">
            <div v-if="field === customField.name">
              <div
                class="input-container"
                :class="{ 'input-error': customField.error && !isFieldFocused(customField.name) }"
              >
                <v-select
                  v-model="customField.value"
                  :options="JSON.parse(customField.values_custom_fields)"
                  style="min-width: 320px; margin-top: 10px"
                  :placeholder="
                    customField.placeholder + (customField.required === 0 ? ' (optional)' : '')
                  "
                  @change="setCustomFieldsValidations(customField)"
                  @focus="handleFocus(customField.name)"
                  @blur="handleBlur(customField.name)"
                />
              </div>
              <div
                v-if="
                  customField.error &&
                  !isFieldFocused(customField.name) &&
                  (validationAttempted || customField.dirty)
                "
                class="error-message"
              >
                {{ getCustomFieldErrorMessage(customField.name) }}
              </div>
            </div>
          </template>
          <template v-else>
            <div v-if="field === customField.name">
              <div
                class="input-container"
                :class="{ 'input-error': customField.error && !isFieldFocused(customField.name) }"
              >
                <input
                  v-model="customField.value"
                  :type="customField.type"
                  placeholder=" "
                  @input="setCustomFieldsValidations(customField)"
                  @focus="handleFocus(customField.name)"
                  @blur="handleBlur(customField.name)"
                />
                <label class="custom-label">
                  <span class="label-text">{{ customField.placeholder }}</span>
                  <span v-if="customField.required === 0" class="label-optional">
                    ({{ $t('optional') }})
                  </span>
                </label>
              </div>
              <div
                v-if="
                  customField.error &&
                  !isFieldFocused(customField.name) &&
                  (validationAttempted || customField.dirty)
                "
                class="error-message"
              >
                {{ getCustomFieldErrorMessage(customField.name) }}
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
  import { required, email } from 'vuelidate/lib/validators'
  import AlertMsg from './../../components/errors/alert.vue'
  import { mask } from 'vue-the-mask'
  import vSelect from 'vue-select'
  import 'vue-select/dist/vue-select.css'
  import Datepicker from 'vuejs-datepicker'
  import * as languages from 'vuejs-datepicker/dist/locale'
  import snakeCase from 'lodash/snakeCase'
  import startCase from 'lodash/startCase'
  import CountrySelector from '../phone/country-selector.vue'
  import { find } from 'lodash'
  import allCountries from '../phone/country-codes.json'
  import vClickOutside from 'v-click-outside'
  import { logUserAction } from '../../utils/logging'
  const touchMap = new WeakMap()
  export default {
    components: {
      AlertMsg,
      vSelect,
      Datepicker,
      CountrySelector,
    },
    directives: {
      mask,
      clickOutside: vClickOutside.directive,
    },
    props: {
      fields: {
        type: Array,
      },
      value: {
        require: true,
      },
      baseData: {
        type: Object,
      },
      consentAccepted: {
        type: Boolean,
        default: undefined,
      },
      pinLength: {
        type: Number,
        default: 4,
      },
    },
    data() {
      return {
        isFocused: false,
        focusedField: null,
        focusedFields: {},
        dynamicCustomFields: {},
        dateLanguages: languages,
        user_email: (this.value && this.value.user_email) || '',
        user_name: (this.value && this.value.user_name) || '',
        user_phone: (this.value && this.value.user_phone) || '',
        user_country: (this.value && this.value.user_country) || '',
        user_city: (this.value && this.value.user_city) || '',
        countries: [
          'Afghanistan',
          'Albania',
          'Algeria',
          'American Samoa',
          'Andorra',
          'Angola',
          'Anguilla',
          'Antarctica',
          'Antigua and Barbuda',
          'Argentina',
          'Armenia',
          'Aruba',
          'Australia',
          'Austria',
          'Azerbaijan',
          'Bahamas',
          'Bahrain',
          'Bangladesh',
          'Barbados',
          'Belarus',
          'Belgium',
          'Belize',
          'Benin',
          'Bermuda',
          'Bhutan',
          'Bolivia',
          'Bosnia and Herzegowina',
          'Botswana',
          'Bouvet Island',
          'Brazil',
          'British Indian Ocean Territory',
          'Brunei Darussalam',
          'Bulgaria',
          'Burkina Faso',
          'Burundi',
          'Cambodia',
          'Cameroon',
          'Canada',
          'Cape Verde',
          'Cayman Islands',
          'Central African Republic',
          'Chad',
          'Chile',
          'China',
          'Christmas Island',
          'Cocos (Keeling) Islands',
          'Colombia',
          'Comoros',
          'Congo',
          'Congo, the Democratic Republic of the',
          'Cook Islands',
          'Costa Rica',
          "Cote d'Ivoire",
          'Croatia (Hrvatska)',
          'Cuba',
          'Cyprus',
          'Czech Republic',
          'Denmark',
          'Djibouti',
          'Dominica',
          'Dominican Republic',
          'East Timor',
          'Ecuador',
          'Egypt',
          'El Salvador',
          'Equatorial Guinea',
          'Eritrea',
          'Estonia',
          'Ethiopia',
          'Falkland Islands (Malvinas)',
          'Faroe Islands',
          'Fiji',
          'Finland',
          'France',
          'France Metropolitan',
          'French Guiana',
          'French Polynesia',
          'French Southern Territories',
          'Gabon',
          'Gambia',
          'Georgia',
          'Germany',
          'Ghana',
          'Gibraltar',
          'Greece',
          'Greenland',
          'Grenada',
          'Guadeloupe',
          'Guam',
          'Guatemala',
          'Guinea',
          'Guinea-Bissau',
          'Guyana',
          'Haiti',
          'Heard and Mc Donald Islands',
          'Holy See (Vatican City State)',
          'Honduras',
          'Hong Kong',
          'Hungary',
          'Iceland',
          'India',
          'Indonesia',
          'Iran (Islamic Republic of)',
          'Iraq',
          'Ireland',
          'Israel',
          'Italy',
          'Jamaica',
          'Japan',
          'Jordan',
          'Kazakhstan',
          'Kenya',
          'Kiribati',
          "Korea, Democratic People's Republic of",
          'Korea, Republic of',
          'Kuwait',
          'Kyrgyzstan',
          "Lao, People's Democratic Republic",
          'Latvia',
          'Lebanon',
          'Lesotho',
          'Liberia',
          'Libyan Arab Jamahiriya',
          'Liechtenstein',
          'Lithuania',
          'Luxembourg',
          'Macau',
          'Macedonia, The Former Yugoslav Republic of',
          'Madagascar',
          'Malawi',
          'Malaysia',
          'Maldives',
          'Mali',
          'Malta',
          'Marshall Islands',
          'Martinique',
          'Mauritania',
          'Mauritius',
          'Mayotte',
          'Mexico',
          'Micronesia, Federated States of',
          'Moldova, Republic of',
          'Monaco',
          'Mongolia',
          'Montserrat',
          'Morocco',
          'Mozambique',
          'Myanmar',
          'Namibia',
          'Nauru',
          'Nepal',
          'Netherlands',
          'Netherlands Antilles',
          'New Caledonia',
          'New Zealand',
          'Nicaragua',
          'Niger',
          'Nigeria',
          'Niue',
          'Norfolk Island',
          'Northern Mariana Islands',
          'Norway',
          'Oman',
          'Pakistan',
          'Palau',
          'Panama',
          'Papua New Guinea',
          'Paraguay',
          'Peru',
          'Philippines',
          'Pitcairn',
          'Poland',
          'Portugal',
          'Puerto Rico',
          'Qatar',
          'Reunion',
          'Romania',
          'Russian Federation',
          'Rwanda',
          'Saint Kitts and Nevis',
          'Saint Lucia',
          'Saint Vincent and the Grenadines',
          'Samoa',
          'San Marino',
          'Sao Tome and Principe',
          'Saudi Arabia',
          'Senegal',
          'Seychelles',
          'Sierra Leone',
          'Singapore',
          'Slovakia (Slovak Republic)',
          'Slovenia',
          'Solomon Islands',
          'Somalia',
          'South Africa',
          'South Georgia and the South Sandwich Islands',
          'Spain',
          'Sri Lanka',
          'St. Helena',
          'St. Pierre and Miquelon',
          'Sudan',
          'Suriname',
          'Svalbard and Jan Mayen Islands',
          'Swaziland',
          'Sweden',
          'Switzerland',
          'Syrian Arab Republic',
          'Taiwan, Province of China',
          'Tajikistan',
          'Tanzania, United Republic of',
          'Thailand',
          'Togo',
          'Tokelau',
          'Tonga',
          'Trinidad and Tobago',
          'Tunisia',
          'Turkey',
          'Turkmenistan',
          'Turks and Caicos Islands',
          'Tuvalu',
          'Uganda',
          'Ukraine',
          'United Arab Emirates',
          'United Kingdom',
          'United States',
          'United States Minor Outlying Islands',
          'Uruguay',
          'Uzbekistan',
          'Vanuatu',
          'Venezuela',
          'Vietnam',
          'Virgin Islands (British)',
          'Virgin Islands (U.S.)',
          'Wallis and Futuna Islands',
          'Western Sahara',
          'Yemen',
          'Yugoslavia',
          'Zambia',
          'Zimbabwe',
        ],
        user_dob: (this.value && this.value.user_dob) || '',
        gender: (this.value && this.value.gender) || '',
        code: '+1',
        country: '',
        flag: '',
        phone_pin: '',
        phone: {},
        validPhone: true,
        birthday: (this.value && this.value.user_dob) || '',
        customFieldNames: [],
        customFields: [
          {
            name: 'Name',
            field: 'user_name',
            validation: { required },
          },
          {
            name: 'Email',
            field: 'user_email',
            validation:
              this.consentAccepted === undefined
                ? { required, email: this.customEmailValidator }
                : this.consentAccepted
                ? { email: this.customEmailValidator }
                : { required, email: this.customEmailValidator },
          },
          {
            name: 'Phone Number',
            field: 'user_phone',
            validation: { required },
          },
          {
            name: 'Phone',
            field: 'user_phone',
            validation: { required },
          },
          {
            name: 'Country',
            field: 'user_country',
            validation: { required },
          },
          {
            name: 'City',
            field: 'user_city',
            validation: { required },
          },
          {
            name: 'Gender',
            field: 'gender',
            validation: { required },
          },
          {
            name: 'Year Born',
            field: 'user_dob',
            validation: { required },
          },
          {
            name: 'Date of Birth',
            field: 'user_dob',
            validation: { required },
          },
          {
            name: 'DOB',
            field: 'user_dob',
            validation: { required },
          },
          {
            name: 'Phone PIN',
            field: 'phone_pin',
            validation: { required },
          },
        ],
        userCreatedCustomFields: [],
        countryModal: false,
        showMessage: {},
        clickTimer: {},
        isCountrySelectOpen: false,
        isDatePickerOpen: false,
        validationAttempted: false,
      }
    },
    computed: {
      pinMask() {
        return '#'.repeat(this.pinLength)
      },
      datePickerLanguage() {
        if (this.dateLanguages[this.lang]) {
          return this.dateLanguages[this.lang]
        }
        return this.dateLanguages.en
      },
      lang() {
        return this.$i18n.locale
      },
      valid() {
        return !this.$v.$invalid
      },
      customFieldsPayload() {
        return this.baseData.loginOptions.customFields
      },
      payload() {
        const payload = {
          customfields: {},
        }
        let value = null
        this.fields.forEach((a, i) => {
          value = this.customFields.find((item) => item.name === a)
          if (!!value && this.existingCustomFields.includes(value.field)) {
            payload[value.field] = this[value.field]
            if (value.field === 'user_phone') {
              payload.country = this.code.replace(/\D/g, '')
            }
          } else {
            this.userCreatedCustomFields.forEach((field) => {
              if (snakeCase(a) === field.field) {
                payload.customfields[field.id] = field.value || ''
              }
            })
          }
        })
        return payload
      },
      existingCustomFields() {
        return [
          'user_name',
          'user_email',
          'user_phone',
          'user_phone',
          'user_country',
          'user_city',
          'gender',
          'user_dob',
          'user_dob',
          'user_dob',
          'phone_pin',
        ]
      },
      isUserCreatedCustomFieldsHasError() {
        const checkIfAnyError = this.userCreatedCustomFields.map((field) => !!field.error)
        return checkIfAnyError.includes(true)
      },
      getCustomFieldErrorMessage() {
        return (fieldName) => {
          if (!fieldName) return this.$t('custom_field_error', { fieldName: 'Field' })

          const snakeCaseName = fieldName.toLowerCase().replace(/\s+/g, '_')
          const translationKey = `custom_field_error_${snakeCaseName}`

          // Try to get the custom translation
          const customTranslation = this.$t(translationKey)

          // If the translation is not found (returns the key itself), use fallback
          if (customTranslation === translationKey) {
            return this.$t('custom_field_error', { fieldName: this.upcaseName(fieldName) })
          }

          return customTranslation
        }
      },
    },
    watch: {
      payload() {
        this.$emit('input', this.payload)
      },
      birthday() {
        if (this.birthday) {
          this.user_dob = moment(this.birthday).format('MM/DD/YYYY')
        } else {
          this.user_dob = ''
        }
      },
      customFieldsPayload: {
        deep: true,
        handler() {
          if (
            this.baseData.loginOptions.customFields &&
            this.baseData.loginOptions.customFields.length > 0
          ) {
            this.userCreatedCustomFields = this.baseData.loginOptions.customFields
          } else {
            this.userCreatedCustomFields = []
          }
        },
      },
    },
    mounted() {
      if (
        this.baseData.loginOptions.customFields &&
        this.baseData.loginOptions.customFields.length > 0
      ) {
        this.userCreatedCustomFields = this.baseData.loginOptions.customFields
      }
      this.checkRegion()
      this.checkUserCustomFields()
    },
    validations() {
      const rules = {}
      let value = null
      this.fields.forEach((item, index) => {
        value = this.customFields.find((a) => a.name === item)
        if (!value) return
        rules[value.field] = value.validation
        if (value.field === 'user_phone') {
          rules.code = { required }
        }
      })
      return rules
    },
    methods: {
      openCountryModal() {
        logUserAction({ type: 'click', label: 'Open Country Modal' })
        this.countryModal = true
      },
      onSelect(country) {
        this.code = country.dialCode
        this.country = country.code
        this.flag = country.flag
        this.user_phone = ''
        setTimeout(() => {
          this.countryModal = false
        }, 700)
        this.$v.$reset()
      },
      setCustomFieldsValidations(field) {
        // Mark field as dirty when user interacts with it
        field.dirty = true

        if (field.required) {
          !field.value ? (field.error = true) : (field.error = false)
        }
      },
      upcaseName(name) {
        return startCase(name)
      },
      checkUserCustomFields() {
        if (this.userCreatedCustomFields.length > 0) {
          this.userCreatedCustomFields = this.userCreatedCustomFields.map((customField) => {
            customField.field = snakeCase(customField.name)
            customField.error = false
            customField.dirty = false
            return customField
          })
        }
      },
      checkRegion() {
        if (this.baseData.advancedOptions && this.baseData.advancedOptions.country.dialCode) {
          this.code = this.baseData.advancedOptions.country.dialCode
          this.updateFlag(this.code)
        }
      },
      async loadCountries() {
        const countries = await import('../phone/country-codes.js')
        return countries
      },
      async updateFlag(code) {
        if (allCountries) {
          const country = find(allCountries, function (o) {
            return o.dialCode === code
          })
          if (country && country.flag) {
            this.flag = country.flag
          }
        }
      },
      delayTouch($v) {
        $v.$reset()
        if (touchMap.has($v)) {
          clearTimeout(touchMap.get($v))
        }
        touchMap.set($v, setTimeout($v.$touch, 500))
      },
      isPhone(field) {
        return ['Phone', 'Phone Number'].includes(field)
      },
      allValid() {
        this.validationAttempted = true
        this.runAllChecks()
        // Not so smart solution to wait for 1s to finish validation.
        // Need to wait for vuelidate to support async validation on delayTouch
        setTimeout(() => {
          this.valid && !this.isUserCreatedCustomFieldsHasError
            ? this.$emit('valid')
            : this.$emit('invalid')
        }, 1000)
      },
      runAllChecks() {
        this.userCreatedCustomFields = this.userCreatedCustomFields.map((field) => {
          field.error = false
          return field
        })
        setTimeout(() => {
          this.userCreatedCustomFields = this.userCreatedCustomFields.map((field) => {
            if (field.required) {
              field.value ? (field.error = false) : (field.error = true)
            }
            return field
          })
          this.fields.forEach((field, i) => {
            this.customFields.find((item) => item.name === field) &&
              this.delayTouch(this.$v[this.customFields.find((item) => item.name === field).field])
            if (this.isPhone(field)) {
              this.delayTouch(this.$v.code)
            }
          })
          if (this.fields.includes('Gender')) {
            this.delayTouch(this.$v.gender)
          }
        }, 1000)
      },
      numberFormat() {
        let input = this.user_phone.replace(/\D/g, '')
        if (this.code === '+1') {
          input = input.substring(0, 10)
          const size = input.length
          if (size < 4) {
            this.user_phone = `(${input}`
          } else if (size < 7) {
            this.user_phone = `(${input.substring(0, 3)}) ${input.substring(3)}`
          } else if (size < 11) {
            this.user_phone = `(${input.substring(0, 3)}) ${input.substring(
              3,
              6,
            )} - ${input.substring(6)}`
          } else {
            this.user_phone = `(${input.substring(0, 3)}) ${input.substring(
              3,
              7,
            )} - ${input.substring(7, 12)}`
          }
          this.isCanadianPhone()
          return
        }
        const countryCodeDigits = this.code.replace(/\D/g, '').length
        const maxLength = 15 - countryCodeDigits
        this.user_phone = input.replace(/^0+/, '').substring(0, maxLength)
      },
      isCanadianPhone() {
        const areaCode = this.user_phone.replace(/\D/g, '').substring(0, 3)
        const canadianAreaCodes = [
          '403',
          '587',
          '780',
          '825', // Alberta
          '236',
          '250',
          '604',
          '672',
          '778', // British Columbia
          '204',
          '431', // Manitoba
          '506', // New Brunswick
          '709', // Newfoundland and Labrador
          '867', // Northwest Territories, Yukon, Nunavut
          '782',
          '902', // Nova Scotia and Prince Edward Island
          '226',
          '249',
          '289',
          '343',
          '365',
          '416',
          '437',
          '519',
          '548',
          '613',
          '647',
          '705',
          '753',
          '807',
          '905', // Ontario
          '367',
          '418',
          '438',
          '450',
          '514',
          '579',
          '581',
          '819',
          '873', // Quebec
          '306',
          '474',
          '639', // Saskatchewan
        ]
        const countryCode = canadianAreaCodes.includes(areaCode) ? 'CA' : 'US'
        const country = find(allCountries, function (o) {
          return o.code === countryCode
        })
        if (country && country.flag) {
          this.flag = country.flag
        }
      },
      scrollToDatePicker() {
        const datePicker = this.$refs.dobDatepicker?.$el || document.querySelector('.Date-picker')
        if (datePicker) {
          datePicker.scrollIntoView && datePicker.scrollIntoView()
        }

        // Fix z-index issue: Calculate fixed position for calendar when in error state
        const inputContainer = datePicker?.closest
          ? datePicker.closest('.input-container.input-error')
          : document.querySelector('.input-container.input-error')

        if (inputContainer) {
          const fixCalendarPosition = () => {
            // Search for calendar - CORRECT CLASS: vdp-datepicker_calendar (single underscore)
            let calendar = document.querySelector(
              '.vdp-datepicker_calendar:not([style*="display: none"])',
            )

            // If not found, search within the datepicker component
            if (!calendar && this.$refs.dobDatepicker) {
              const datepickerEl = this.$refs.dobDatepicker.$el
              calendar = datepickerEl?.querySelector('.vdp-datepicker_calendar')
            }

            // Search in input container
            if (!calendar && inputContainer) {
              calendar = inputContainer.querySelector('.vdp-datepicker_calendar')
            }

            // Last resort: search entire document for visible calendar
            if (!calendar) {
              const allCalendars = document.querySelectorAll('.vdp-datepicker_calendar')
              calendar = Array.from(allCalendars).find((el) => {
                const style = window.getComputedStyle(el)
                return style.display !== 'none' && style.visibility !== 'hidden'
              })
            }

            if (calendar) {
              const rect = inputContainer.getBoundingClientRect()
              // Set fixed position to escape stacking context
              calendar.style.setProperty('position', 'fixed', 'important')
              calendar.style.setProperty('top', `${rect.bottom + 2}px`, 'important')
              calendar.style.setProperty('left', `${rect.left}px`, 'important')
              calendar.style.setProperty('width', `${rect.width}px`, 'important')
              calendar.style.setProperty('z-index', '2147483647', 'important') // Maximum z-index
              calendar.style.setProperty('margin-left', '0', 'important')

              // Also ensure parent elements don't create stacking context
              const datePickerEl = calendar.closest('.Date-picker')
              if (datePickerEl) {
                datePickerEl.style.setProperty('z-index', '2147483647', 'important')
                datePickerEl.style.setProperty('transform', 'none', 'important')
              }

              // Ensure input-container doesn't trap
              if (inputContainer) {
                inputContainer.style.setProperty('z-index', '2147483646', 'important')
                inputContainer.style.setProperty('transform', 'none', 'important')
              }

              // Lower z-index of all subsequent custom-field-containers (fields below DOB)
              const dobContainer = inputContainer.closest('.custom-field-container')
              if (dobContainer) {
                let nextSibling = dobContainer.nextElementSibling
                while (nextSibling) {
                  if (nextSibling.classList.contains('custom-field-container')) {
                    nextSibling.style.setProperty('z-index', '1', 'important')
                    // Lower z-index of child elements too
                    const genderDiv = nextSibling.querySelector('.gender-div')
                    const inputContainers = nextSibling.querySelectorAll('.input-container')
                    const errorMessages = nextSibling.querySelectorAll('.error-message')

                    if (genderDiv) genderDiv.style.setProperty('z-index', '1', 'important')
                    inputContainers.forEach((el) =>
                      el.style.setProperty('z-index', '1', 'important'),
                    )
                    errorMessages.forEach((el) => el.style.setProperty('z-index', '0', 'important'))
                  }
                  nextSibling = nextSibling.nextElementSibling
                }
              }
            }
          }

          // Try immediately and with delays
          this.$nextTick(() => {
            fixCalendarPosition()
            setTimeout(fixCalendarPosition, 50)
            setTimeout(fixCalendarPosition, 150)
            setTimeout(fixCalendarPosition, 300)
          })
        }
      },
      handleDobInput() {
        this.delayTouch(this.$v.user_dob)
      },
      formatDate(date) {
        if (!date) return null
        if (typeof date === 'string') return date
        if (date instanceof Date) {
          return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
        }
        return null
      },
      validateNumber() {
        if (!this.phone.valid) {
          this.validPhone = false
        }
      },
      closeCountryModal() {
        this.countryModal = false
      },
      placeholder(option, fieldname) {
        const field = this.customFields.find((field) => field.name === fieldname)
        const placeholder =
          this.$t(option) +
          (['DOB', 'Date of Birth', 'Year Born'].includes(field.name) ? '(mm/dd/yyyy)' : '') +
          (field.validation && field.validation.required ? '' : ' (' + this.$t('optional') + ')')
        return placeholder
      },
      isOptionalFields(fieldname) {
        const field = this.customFields.find((field) => field.name === fieldname)
        return !(field.validation && field.validation.required)
      },
      handleClickOutside() {
        this.isDatePickerOpen = false
        // Reset calendar position when closed
        this.$nextTick(() => {
          const datePicker = document.querySelector('.Date-picker')
          const inputContainer = datePicker?.closest('.input-container.input-error')

          // Search for calendar more broadly - CORRECT CLASS: vdp-datepicker_calendar
          let calendar = inputContainer?.querySelector('.vdp-datepicker_calendar')
          if (!calendar) {
            calendar = document.querySelector('.vdp-datepicker_calendar')
          }

          if (calendar && calendar.style.position === 'fixed') {
            // Reset to absolute positioning
            calendar.style.position = ''
            calendar.style.top = ''
            calendar.style.left = ''
            calendar.style.width = ''
            calendar.style.zIndex = ''
            calendar.style.marginLeft = ''
          }

          // Reset z-index of subsequent fields when calendar closes
          const dobContainer = inputContainer?.closest('.custom-field-container')
          if (dobContainer) {
            let nextSibling = dobContainer.nextElementSibling
            while (nextSibling) {
              if (nextSibling.classList.contains('custom-field-container')) {
                nextSibling.style.removeProperty('z-index')
                const genderDiv = nextSibling.querySelector('.gender-div')
                const inputContainers = nextSibling.querySelectorAll('.input-container')
                const errorMessages = nextSibling.querySelectorAll('.error-message')

                if (genderDiv) genderDiv.style.removeProperty('z-index')
                inputContainers.forEach((el) => el.style.removeProperty('z-index'))
                errorMessages.forEach((el) => el.style.removeProperty('z-index'))
              }
              nextSibling = nextSibling.nextElementSibling
            }
          }
        })
      },
      handleFocus(fieldName) {
        this.$set(this.focusedFields, fieldName, true)
        this.isFocused = true
        this.focusedField = fieldName
      },
      handleBlur(fieldName) {
        this.$set(this.focusedFields, fieldName, false)
        this.isFocused = false
        this.focusedField = null
      },
      isFieldFocused(fieldName) {
        return this.focusedFields[fieldName] === true
      },
      customEmailValidator(value) {
        if (!value) return true
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) return false

        const [localPart, domain] = value.split('@')
        if (!localPart || !domain) return false

        if (localPart.length < 2 || localPart.length > 64) return false
        if (value.length > 254) return false

        const domainParts = domain.split('.')
        if (domainParts.length < 2) return false
        if (domainParts.some((part) => part.length === 0)) return false

        const tld = domainParts[domainParts.length - 1]
        if (tld.length < 2) return false
        if (domain.length < 4) return false

        if (value.includes('..')) return false

        return true
      },
    },
  }
</script>
<style scoped>
  .custom-field-container {
    margin: 10px 0 0 0;
  }

  .custom-field-container:has(.error-message) + .custom-field-container {
    margin-top: 5px;
  }

  .country-select {
    width: 100%;
    border: none;
    outline: none;
    justify-content: center;
    align-items: center;
    margin: auto;
    height: 40px;
  }
  .div-error {
    border-color: red;
    background: #f7eaea;
  }
  .div-error {
    animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
  }
  .div-error >>> .vs__dropdown-toggle {
    background: border-box;
  }
  .country-select >>> .vs__dropdown-toggle {
    border: none !important;
    height: 38px;
    border-radius: 32px;
    justify-content: center;
    align-items: center;
    padding-left: 10px;
  }
  .country-select >>> .vs__search {
    width: 20px !important;
    border: none !important;
    box-shadow: none !important;
    padding-top: 30px !important;
    padding-left: 6px !important;
  }
  .country-select >>> .vs__selected-options {
    align-items: center !important;
  }
  .country-select >>> .vs__clear {
    display: none;
  }
  .country-select >>> .vs__actions {
    margin-right: 15px;
  }
  .country-select >>> .vs__selected {
    position: absolute;
    top: 23px;
  }
  .country-select.has-value >>> .vs__selected {
    padding-top: 5px;
  }
  .vs__dropdown-menu {
    top: 232px !important;
    left: 17px !important;
    width: 316.469px !important;
    overflow-x: clip !important;
  }
  .input-container {
    position: relative;
  }
  .gender-div {
    max-height: 52px;
    padding: 0;
    border: 1px solid #ccc;
    background: #ffffff;
    border-radius: 32px;
    box-shadow: 2px 0 20px 0 rgba(0, 0, 0, 0.1);
    position: relative;
  }

  .custom-select .vs__dropdown-toggle {
    padding-top: 1.2em;
    padding-bottom: 0.2em;
  }

  .error-message {
    color: #ff4444 !important;
    font-size: 11px;
    margin-top: 2px;
    margin-left: 2px;
    text-align: left;
    padding-left: 20px;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
  }
</style>

<style lang="scss">
  .select-vue {
    width: 100%;
    .dropdown {
      width: 100%;
    }
  }
  .Date-picker {
    width: 100%;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    position: relative;
    input {
      width: 100%;
      border: none;
      background: transparent;
      outline: none;
      font-size: 16px;
      padding: 10px 20px;
      border-radius: 32px;
      color: #000000;
      text-align: left;
    }

    input::placeholder {
      color: transparent;
    }
    .vdp-datepicker_calendar {
      position: absolute;
      top: 50px;
      border-radius: 5px;
      min-width: 100%;
      margin-left: 40px;
      z-index: 2147483647; /* Maximum z-index value */
      background: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      border: 1px solid #ddd;
      .cell {
        font-size: 14px;
        &:hover {
          border: 1px solid #55acee;
          border-radius: 5px;
        }
        &.selected {
          background: #55acee;
          border-radius: 5px;
          color: white;
          &:hover {
            background: #55acee;
          }
        }
      }
    }
  }
  .phone-flag {
    display: flex;
    border-radius: 32px;
    justify-content: center;
    align-items: center;
    min-width: 60px;
    max-height: 50px !important;
    padding: 0 10px;
    margin: 0 3px 0 0;
    box-shadow: 2px 0 20px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #ccc;
    cursor: pointer;
    background: #ffffff;
    color: #000000;
  }
  .phone-input {
    max-width: 193px !important;
    max-height: 18px !important;
  }
  .custom-field-container {
    width: 100%;
  }
  .input-error .Date-picker {
    z-index: 2147483647 !important;
    position: relative !important;
    transform: none !important;
    isolation: auto !important;
  }

  .input-container.input-error {
    z-index: 2147483646 !important;
    transform: none !important;
  }

  .custom-field-container:has(.input-error) {
    z-index: 2147483645 !important;
    position: relative !important;
  }

  .input-error .Date-picker .vdp-datepicker_calendar,
  .input-error .vdp-datepicker_calendar,
  .input-container.input-error .vdp-datepicker_calendar,
  .custom-field-container:has(.input-error) .vdp-datepicker_calendar,
  .input-error ~ * .vdp-datepicker_calendar {
    z-index: 2147483647 !important;
    position: fixed !important;
  }

  body:has(.input-container.input-error) .vdp-datepicker_calendar,
  .custom-fields .vdp-datepicker_calendar {
    z-index: 2147483647 !important;
  }
  .Date-picker:focus-within ~ * {
    opacity: 0.3 !important;
    pointer-events: none !important;
  }

  .custom-field-container:has(.Date-picker:focus-within) ~ .custom-field-container,
  .custom-field-container:has(.vdp-datepicker_calendar) ~ .custom-field-container {
    z-index: 1 !important;
    position: relative !important;
  }

  .custom-field-container:has(.Date-picker:focus-within) ~ .custom-field-container .gender-div,
  .custom-field-container:has(.Date-picker:focus-within) ~ .custom-field-container .input-container,
  .custom-field-container:has(.vdp-datepicker_calendar) ~ .custom-field-container .gender-div,
  .custom-field-container:has(.vdp-datepicker_calendar) ~ .custom-field-container .input-container {
    z-index: 1 !important;
    position: relative !important;
  }

  .custom-field-container:has(.Date-picker:focus-within) ~ .custom-field-container .error-message,
  .custom-field-container:has(.vdp-datepicker_calendar) ~ .custom-field-container .error-message {
    z-index: 0 !important;
  }
  .Date-picker .vdp-datepicker_calendar {
    background: white !important;
    border: 1px solid #ddd !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    z-index: 2147483647 !important; /* Maximum z-index value */
    position: absolute !important;
    top: 52px !important;
    left: 0 !important;
    width: 100% !important;
    margin-left: 0 !important;
  }

  .custom-field-container .error-message {
    z-index: -1 !important;
    position: relative !important;
  }

  .custom-field-container:has(.vdp-datepicker_calendar) .error-message,
  body:has(.vdp-datepicker_calendar) .error-message {
    z-index: -1 !important;
  }
  .input-container .Date-picker input:not(:placeholder-shown),
  .input-container.has-value .Date-picker input {
    padding-top: 18px;
  }

  .input-container .Date-picker input:not(:placeholder-shown) + .custom-label,
  .input-container.has-value .custom-label {
    top: 10px;
    left: 20px;
    font-size: 11px;
  }

  /* Ensure DOB label is always visible and properly positioned */
  .input-container .Date-picker + .custom-label {
    position: absolute !important;
    top: 50% !important;
    left: 20px !important;
    transform: translateY(-50%) !important;
    font-size: 16px !important;
    transition: all 0.2s ease !important;
    pointer-events: none !important;
    color: #7b7b7b !important;
    z-index: 10 !important;
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    align-items: center !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* Show label at top when Datepicker has a value or is focused */
  .input-container.has-value .Date-picker + .custom-label,
  .input-container .Date-picker:focus-within + .custom-label {
    top: 4px !important;
    left: 20px !important;
    font-size: 11px !important;
    transform: none !important;
    color: #7b7b7b !important;
  }

  /* Ensure label is visible even when input has error state - show at center if no value */
  .input-error .Date-picker + .custom-label {
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    font-size: 16px !important;
  }

  /* If error state but has value, show label at top */
  .input-error.has-value .Date-picker + .custom-label {
    top: 10px !important;
    transform: none !important;
    font-size: 11px !important;
  }

  /* Ensure Datepicker input doesn't interfere with label visibility */
  .input-container .Date-picker input {
    background: transparent !important;
  }

  /* Hide any default placeholder text from Datepicker */
  .input-container .Date-picker input::placeholder {
    color: transparent !important;
    opacity: 0 !important;
  }

  /* Force label to show when Datepicker input is empty - this is the key fix */
  .input-container:not(.has-value) .Date-picker + .custom-label {
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    font-size: 16px !important;
  }

  /* Ensure label shows even in error state when no value */
  .input-error:not(.has-value) .Date-picker + .custom-label {
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    font-size: 16px !important;
  }
  .country-select {
    position: relative;
  }
  .country-select .custom-label {
    position: absolute !important;
    top: 50% !important;
    left: 20px !important;
    transform: translateY(-50%) !important;
    font-size: 16px !important;
    transition: all 0.2s ease !important;
    pointer-events: none !important;
    color: #7b7b7b !important;
    z-index: 1 !important;
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    align-items: center !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  .country-select.has-value .custom-label {
    top: 1px !important;
    left: 20px !important;
    font-size: 11px !important;
    transform: none !important;
    color: #7b7b7b !important;
  }
  .country-select:focus-within .custom-label {
    top: 10px !important;
    left: 20px !important;
    font-size: 11px !important;
    transform: none !important;
    color: #7b7b7b !important;
  }
  .country-select .custom-label {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
</style>
